"use client";
import classNames from 'classnames';
import dynamic from "next/dynamic";
import { useState } from "react";
import { useMovieActivityContext } from "src/components/film/filmwebid/MovieActivityContextTypes";
import RatingPanel from "src/components/filmwebid/RatingPanel";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import HeartSvg from "src/components/svg/HeartSvg";
import SpeechBubbleSvg from "src/components/svg/SpeechBubbleSvg";
import StarSvg from "src/components/svg/StarSvg";
import WatchlistSvg from "src/components/svg/WatchlistSvg";
import { useUserContext } from "src/lib/contexts/ContextTypes";
import useFavourite from "src/lib/filmwebid/useFavourite";
import useWatchlist from "src/lib/filmwebid/useWatchlist";
import type { IFilmwebIdMovieIdentifier } from "src/lib/filmwebid/useMovieIdentifier";
import styles from './FilmwebIdButtonPanel.module.css';
import getThemeStyles from "src/lib/styling/getThemeStyles";
import { locale } from "@locale";

const ReviewPanel = dynamic(() => import('src/components/filmwebid/ReviewPanel'), {
    loading: () => <LoadingIndicator />,
    ssr: false
});

//#region [Props]
type FilmwebIdButtonPanelProps = {
    movieIdentifier: IFilmwebIdMovieIdentifier | null | undefined;
    hideUserInteractionStatus?: boolean;
    hideGiveRating?: boolean;
    hideCommenting?: boolean;
    className?: string;
    maxUserRatingValue: number;
};
//#endregion

interface IWatchListStatus {
    isWatchlisted: boolean;
    watchlistCount: number;
};

interface IFavouriteStatus {
    isFav: boolean;
    favCount: number;
}

//#region [Component]
export default function FilmwebIdButtonPanel({ movieIdentifier, hideUserInteractionStatus = false, className, hideCommenting = false, hideGiveRating = false, maxUserRatingValue }: FilmwebIdButtonPanelProps) {
    const userContext = useUserContext();
    const movieActivityContext = useMovieActivityContext();
    const [watchlister, _isWatchlisting] = useWatchlist({ movieIdentifier });
    const [favouriter, _isFavving] = useFavourite({ movieIdentifier });
    const [showRatingPanel, setShowRatingPanel] = useState(false);
    const [showReviewPanel, setShowReviewPanel] = useState(false);

    // Maintain local state here since we want to show that something has changed immediately without wating for saving to complete
    const watchlistStatus: IWatchListStatus = {
        isWatchlisted: movieActivityContext.activity?.movieUserInteraction?.watchlisted ?? false,
        watchlistCount: movieActivityContext.activity?.movieStats?.watchlistCount ?? 0
    };

    const favStatus: IFavouriteStatus = {
        isFav: movieActivityContext.activity?.movieUserInteraction?.favourite ?? false,
        favCount: movieActivityContext.activity?.movieStats?.favouriteCount ?? 0
    };

    if (!movieIdentifier) {
        return null;
    }

    // event handlers (created here for readability)
    const onWatchlistClick = () => {
        watchlister(!watchlistStatus.isWatchlisted);
    };

    const onFavClick = () => {
        favouriter(!favStatus.isFav);
    };

    const onRatingPanelClick = () => {
        if (userContext.isAuthenticated) {
            setShowRatingPanel(true);
        } else {
            userContext.doLogin();
        }
    }

    const onReviewPanelClick = () => {
        if (userContext.isAuthenticated) {
            setShowReviewPanel(true);
        } else {
            userContext.doLogin();
        }
    }

    const onRatingPanelClose = (doUpdate: boolean) => {
        setShowRatingPanel(false);
        if (doUpdate) {
            movieActivityContext.activityUpdater();
        }
    }

    const onReviewPanelClose = (doUpdate: boolean) => {
        setShowReviewPanel(false);
        if (doUpdate) {
            movieActivityContext.activityUpdater();
        }
    }

    const themeStyles = getThemeStyles(styles);

    return <div className={classNames(styles.SFilmwebIdButtonPanel, className, themeStyles)}>
        <button title={userContext.isAuthenticated ?  locale.watchlist.addToWatchlist : locale.watchlist.signInToWatchlist}
            className={classNames(styles.SFilmwebIdButton, 'watchListButton')}
            onClick={onWatchlistClick}
            disabled={movieActivityContext.isLoading || !movieActivityContext.activity}
            aria-label={locale.watchlist.addToWatchlist}
        >
            <WatchlistSvg selected={watchlistStatus.isWatchlisted} />
            {!hideUserInteractionStatus && <span className={styles.SStatus}>{watchlistStatus.watchlistCount > 0 ? watchlistStatus.watchlistCount : '​'}</span>}
        </button>
        {!hideGiveRating && <button title={locale.ratings.callToAction}
            className={styles.SFilmwebIdButton}
            onClick={onRatingPanelClick}
            disabled={movieActivityContext.isLoading || !movieActivityContext.activity}
            aria-label={locale.ratings.callToAction}
        >
            <StarSvg selected={!!movieActivityContext.activity?.movieUserInteraction?.rating} />
            {!hideUserInteractionStatus && <span className={styles.SStatus}>{(movieActivityContext.activity?.movieStats?.ratingCount ?? 0) > 0 ? `${movieActivityContext.activity?.movieStats?.ratingAverage.toFixed(1)} / ${process.env.NEXT_PUBLIC_MAX_USER_RATING_VALUE}` : '​'}</span>}
        </button>}

        <button title={locale.favorite.callToAction}
            className={classNames(styles.SFilmwebIdButton, 'favButton')}
            onClick={onFavClick}
            disabled={movieActivityContext.isLoading || !movieActivityContext.activity}
            aria-label={locale.favorite.callToAction}
        >
            <HeartSvg selected={favStatus.isFav} />
            {!hideUserInteractionStatus && <span className={styles.SStatus}>{favStatus.favCount > 0 ? favStatus.favCount : '​'}</span>}
        </button>

        {!hideCommenting && <button title={locale.review.callToAction}
            className={styles.SFilmwebIdButton}
            onClick={onReviewPanelClick}
            disabled={movieActivityContext.isLoading || !movieActivityContext.activity}
            aria-label={locale.review.callToAction}
        >
            <SpeechBubbleSvg selected={!!movieActivityContext.activity?.movieUserInteraction?.review} />
            {!hideUserInteractionStatus && <span className={styles.SStatus}>{(movieActivityContext.activity?.movieStats?.reviewCount ?? 0) > 0 ? movieActivityContext.activity?.movieStats?.reviewCount : '​'}</span>}
        </button>}

        {/* the panels are modal dialogs, which the browser shows above the page, so they need no portal */}
        {showReviewPanel && <ReviewPanel
            movieIdentifier={movieIdentifier}
            onClose={onReviewPanelClose}
            reviewId={movieActivityContext.activity?.movieUserInteraction?.reviewId}
            userRating={movieActivityContext.activity?.movieUserInteraction?.rating}
            userReview={movieActivityContext.activity?.movieUserInteraction?.review}
            maxUserRatingValue={maxUserRatingValue}
        />}


        {showRatingPanel && <RatingPanel
            onClose={onRatingPanelClose}
            movieIdentifier={movieIdentifier}
            hasUserReview={!!movieActivityContext.activity?.movieUserInteraction?.review}
            userRating={movieActivityContext.activity?.movieUserInteraction?.rating}
            movieStats={movieActivityContext.activity?.movieStats}
            maxRatingValue={maxUserRatingValue}
        />}

    </div>;
}
//#endregion
